import { Box, Typography } from "@mui/material";

import { ds } from "../theme/ds";
import type { WidgetProps } from "./types";

import { Card, CardLabel } from "./live/card";

// Who does what, as a matrix (exclusivity pitch room, Elijah 2026-10-27). Two
// panels side by side, one per party, so the page keeps three levels: the page
// title, the party (the only heading in a panel), and each area as a small
// uppercase label over compact bullets. Markdown headings stacked down one
// column read as a wall of titles under the H1, which is what this replaces.
// Each party may carry its logo above its name. Under the panels, what both
// sides do together sits as a row of cards, each an icon centred over its text.
export function Responsibilities({ widget }: WidgetProps<"responsibilities">) {
    const { parties, together } = widget.props;

    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
            <Box
                sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", md: `repeat(${parties.length}, minmax(0, 1fr))` },
                    gap: 3,
                    alignItems: "stretch",
                }}
            >
                {parties.map((party) => (
                    <Card key={party.name} sx={{ p: { xs: "20px", md: "28px" } }}>
                        {party.logoUrl && (
                            <Box
                                component="img"
                                src={party.logoUrl}
                                alt={`${party.name} logo`}
                                sx={{ display: "block", height: party.logoHeight ?? 40, maxWidth: 180, objectFit: "contain", objectPosition: "left center", mb: 2 }}
                            />
                        )}
                        <Typography
                            component="h2"
                            sx={{ fontSize: ds.text.xl, fontWeight: 700, color: ds.navy, lineHeight: 1.3, m: 0 }}
                        >
                            {party.name}
                        </Typography>
                        {party.summary && (
                            <Typography sx={{ fontSize: ds.text.sm, color: ds.gray500, mt: 0.5 }}>{party.summary}</Typography>
                        )}
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5, mt: 3 }}>
                            {party.groups.map((group) => (
                                <Box key={group.title}>
                                    <CardLabel>{group.title}</CardLabel>
                                    <Items items={group.items} />
                                </Box>
                            ))}
                        </Box>
                    </Card>
                ))}
            </Box>

            {together && (
                <Box>
                    <CardLabel>{together.title}</CardLabel>
                    <Box
                        sx={{
                            display: "grid",
                            gridTemplateColumns: { xs: "1fr", md: `repeat(${together.items.length}, minmax(0, 1fr))` },
                            gap: 3,
                            mt: 1.5,
                        }}
                    >
                        {together.items.map((raw) => {
                            const item = typeof raw === "string" ? { text: raw } : raw;
                            return (
                                <Card
                                    key={item.text}
                                    sx={{ p: "24px", display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center", gap: 1.5 }}
                                >
                                    {item.icon && (
                                        <Box component="img" src={item.icon} alt="" aria-hidden sx={{ width: 44, height: 44 }} />
                                    )}
                                    <Typography sx={{ fontSize: ds.text.base, color: ds.gray700, lineHeight: 1.55 }}>
                                        {item.text}
                                    </Typography>
                                </Card>
                            );
                        })}
                    </Box>
                </Box>
            )}
        </Box>
    );
}

function Items({ items }: { items: string[] }) {
    return (
        <Box component="ul" sx={{ listStyle: "none", p: 0, m: 0, mt: 1, display: "flex", flexDirection: "column", gap: 1 }}>
            {items.map((item) => (
                <Box
                    component="li"
                    key={item}
                    sx={{ display: "flex", gap: 1.25, fontSize: ds.text.base, color: ds.gray700, lineHeight: 1.55 }}
                >
                    <Box
                        aria-hidden
                        sx={{ flex: "none", width: 6, height: 6, borderRadius: "50%", bgcolor: ds.navy, mt: "0.6em" }}
                    />
                    <span>{item}</span>
                </Box>
            ))}
        </Box>
    );
}
